import React, { useState, useEffect } from 'react';
import $ from 'jquery';
import swal from 'sweetalert2';
import Loading from '../global/loading';
import {
    validatePromo, createCheckout, verifyPayment,
    loadRazorpayCheckout, formatINR, newIdempotencyKey,
} from '../api/billingAPI';
import { getAuthState } from '../../assets/scripts/login-util';

/**
 * Checkout (Phase 8).
 *
 * ==================== THE THREE OUTCOMES ====================
 *
 * A customer who opens Razorpay Checkout ends up in one of three states,
 * and all three have to be handled here:
 *
 *   1. they pay and the handler fires  -> verifyPayment, done
 *   2. they dismiss the window          -> nothing happened, offer retry
 *   3. they pay and we never hear back  -> the browser closed, the network
 *                                          dropped, the tab was killed
 *
 * Case 3 is the one that matters. The webhook completes the activation
 * server-side regardless, so the orderKey is written to sessionStorage
 * BEFORE Checkout opens - paymentStatus.js polls it on the next page load
 * and shows the customer what actually happened. Without that they see a
 * failed purchase and pay twice.
 *
 * ==================== IDEMPOTENCY ====================
 *
 * One key per ATTEMPT, generated before the first call and reused if the
 * user retries the same purchase. Without it a double-clicked Pay button
 * opens two payable orders at Razorpay.
 */

const CheckoutModal = (props) => {
    const { plan, intent = 'NEW', addOnLicenses = 0, onDismissModal, onPaid } = props;

    const [loading,   setLoading]   = useState(false);
    const [quote,     setQuote]     = useState(null);
    const [promoCode, setPromoCode] = useState('');
    const [promo,     setPromo]     = useState(null);
    const [promoErr,  setPromoErr]  = useState(null);
    const [idemKey]                 = useState(newIdempotencyKey);

    useEffect(() => {
        $('#checkout-modal').modal({ backdrop: 'static', keyboard: false });
        $('#checkout-modal').on('hidden.bs.modal', () => onDismissModal());
        $('#checkout-modal').modal('show');

        return () => { $('#checkout-modal').modal('hide'); };
    }, []);

    const onApplyPromo = async () => {
        setPromoErr(null);
        if (!promoCode || promoCode.trim().length < 3) {
            setPromoErr('Enter a promo code.');
            return;
        }
        try {
            setLoading(true);
            const result = await validatePromo({
                code: promoCode.trim(), planCode: plan.code, addOnLicenses, intent,
            });
            setPromo(result);
            setQuote(result);
        } catch (err) {
            // A rejected code is a normal answer, not a failure. Shown
            // inline rather than in a swal - the customer is mid-flow and
            // a modal on top of a modal is hostile.
            setPromo(null);
            setPromoErr(err.message);
        } finally {
            setLoading(false);
        }
    };

    const onRemovePromo = () => {
        setPromo(null);
        setPromoCode('');
        setPromoErr(null);
    };

    const onPay = async () => {
        try {
            setLoading(true);

            const order = await createCheckout({
                intent,
                planCode     : plan.code,
                addOnLicenses,
                promoCode    : promo ? promoCode.trim() : null,
            }, idemKey);

            // WRITTEN BEFORE CHECKOUT OPENS. If the browser dies between
            // here and the handler, this is the only thing that lets the
            // customer be told what happened to their money.
            sessionStorage.setItem('ib_billing_orderKey', order.orderKey);

            const Razorpay = await loadRazorpayCheckout();
            const authUser = getAuthState('user');
            const user     = (authUser && typeof authUser === 'object') ? authUser : {};

            const rzp = new Razorpay({
                key         : order.keyId,
                order_id    : order.gatewayOrderId,
                amount      : order.amountPaise,
                currency    : order.currency,
                name        : 'InsureBox',
                description : `${plan.name} plan`,
                prefill     : { name: [user.firstName, user.lastName].filter(Boolean).join(' '), email: user.email || '' },
                theme       : { color: '#5d78ff' },

                handler: async (response) => {
                    try {
                        setLoading(true);
                        const result = await verifyPayment({
                            orderKey            : order.orderKey,
                            razorpay_order_id   : response.razorpay_order_id,
                            razorpay_payment_id : response.razorpay_payment_id,
                            razorpay_signature  : response.razorpay_signature,
                        });
                        sessionStorage.removeItem('ib_billing_orderKey');
                        $('#checkout-modal').modal('hide');
                        swal.fire({
                            icon              : 'success',
                            titleText         : 'Payment received',
                            html              : `<p>Your subscription is active.</p><p class="kt-font-sm">Invoice ${result.invoiceNo || ''}</p>`,
                            buttonsStyling    : false,
                            confirmButtonClass: 'btn btn-brand',
                        });
                        if (onPaid) onPaid(result);
                    } catch (err) {
                        // The payment may well have succeeded - only our
                        // confirmation failed. Say so rather than implying
                        // the money did not leave their account.
                        swal.fire({
                            icon              : 'warning',
                            titleText         : 'Confirming your payment',
                            text              : 'Your payment went through but we could not confirm it here. It will be applied automatically within a few minutes - please refresh before trying again.',
                            buttonsStyling    : false,
                            confirmButtonClass: 'btn btn-brand',
                        });
                    } finally {
                        setLoading(false);
                    }
                },

                modal: {
                    ondismiss: () => {
                        setLoading(false);
                        // The order stays open server-side. Nothing was
                        // charged, so nothing needs undoing.
                    },
                },
            });

            rzp.on('payment.failed', (resp) => {
                setLoading(false);
                swal.fire({
                    icon              : 'error',
                    titleText         : 'Payment failed',
                    text              : (resp.error && resp.error.description) || 'The payment could not be completed.',
                    buttonsStyling    : false,
                    confirmButtonClass: 'btn btn-brand',
                });
            });

            rzp.open();
            setLoading(false);

        } catch (err) {
            setLoading(false);
            // BILLING_DISABLED is the kill switch, not a fault. Say
            // something a customer can act on.
            const disabled = /BILLING_DISABLED|not available right now/i.test(err.message);
            swal.fire({
                icon              : disabled ? 'info' : 'error',
                titleText         : disabled ? 'Online payment is unavailable' : 'Could not start checkout',
                text              : disabled
                    ? 'Online payment is switched off at the moment. Please contact support to arrange your subscription.'
                    : err.message,
                buttonsStyling    : false,
                confirmButtonClass: 'btn btn-brand',
            });
        }
    };

    const line = (label, value, cls = '') => (
        <div className="row margin-b5">
            <div className="col-7 kt-font-dark-75">{label}</div>
            <div className={`col-5 text-right ${cls}`}>{value}</div>
        </div>
    );

    return (
        <div className="modal fade" id="checkout-modal" tabIndex="-1" role="dialog" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
                <div className="modal-content">
                    {loading && <Loading />}

                    <div className="modal-header">
                        <h5 className="modal-title">Confirm your purchase</h5>
                        <button type="button" className="close" data-dismiss="modal" aria-label="Close"></button>
                    </div>

                    <div className="modal-body">
                        <h5 className="kt-font-brand">{plan.name} plan</h5>
                        <p className="kt-font-sm kt-font-dark-50">
                            {plan.includedLicenses} licence{plan.includedLicenses === 1 ? '' : 's'} included
                            {addOnLicenses > 0 && ` + ${addOnLicenses} additional`}
                        </p>

                        <div className="kt-separator kt-separator--space-sm kt-separator--border-dashed"></div>

                        <div className="form-group">
                            <label className="kt-font-sm">Promo code</label>
                            <div className="input-group">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Enter a code"
                                    value={promoCode}
                                    disabled={!!promo}
                                    onChange={(e) => setPromoCode(e.target.value)} />
                                <div className="input-group-append">
                                    {promo ? (
                                        <button className="btn btn-secondary" type="button" onClick={onRemovePromo}>Remove</button>
                                    ) : (
                                        <button className="btn btn-brand" type="button" onClick={onApplyPromo}>Apply</button>
                                    )}
                                </div>
                            </div>
                            {promoErr && <div className="kt-font-danger kt-font-sm margin-t5">{promoErr}</div>}
                            {promo && <div className="kt-font-success kt-font-sm margin-t5">Code applied.</div>}
                        </div>

                        <div className="kt-separator kt-separator--space-sm kt-separator--border-dashed"></div>

                        {quote ? (<>
                            {line('Subtotal', formatINR(quote.basePaise))}
                            {Number(quote.discountPaise) > 0 &&
                                line('Discount', `− ${formatINR(quote.discountPaise)}`, 'kt-font-success')}
                            {line('GST (18%)', formatINR(quote.taxPaise))}
                            <div className="kt-separator kt-separator--space-sm"></div>
                            {line(<b>Total</b>, <b className="kt-font-brand">{formatINR(quote.totalPaise)}</b>)}
                        </>) : (
                            <p className="kt-font-sm kt-font-dark-50">
                                The exact total, including GST, is shown on the payment window.
                            </p>
                        )}

                        <p className="kt-font-sm kt-font-dark-50 margin-t15">
                            Billed annually. You will receive a GST invoice by email.
                        </p>
                    </div>

                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" data-dismiss="modal">Cancel</button>
                        <button type="button" className="btn btn-brand" onClick={onPay} disabled={loading}>
                            Pay now
                        </button>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default CheckoutModal;
